// react
import { useCallback, useMemo, useState } from 'react';

// mui
import {
  Avatar,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

// models
import {
  CareCircle,
  careRecipientFromCareCircle,
  computeAge,
  dobDisplay,
  otherCarerMembersFromCareCircle,
  parseConditions,
} from '@karehero/models';

const calcUpdates = (headerDrafts: any, careAssessmentData: any) => {
  if (!careAssessmentData || !headerDrafts) {
    return {};
  }

  const updates: Record<string, string> = {};

  // Conditions
  const conditions = parseConditions(
    careAssessmentData?.answers?.[
      'care-recipient-any-illnesses-or-disabilities'
    ],
  );
  const draftConditionsArr = (headerDrafts.conditions || '')
    .split(',')
    .map((s: any) => s.trim())
    .filter(Boolean);
  if (draftConditionsArr.join(', ') !== conditions.join(', ')) {
    updates['care-recipient-any-illnesses-or-disabilities'] =
      JSON.stringify(draftConditionsArr);
  }

  // Date of birth
  const dob =
    careAssessmentData?.answers?.['care-recipient-date-of-birth'] ?? '';
  if (headerDrafts.dob !== dob) {
    updates['care-recipient-date-of-birth'] = headerDrafts.dob;
  }

  // Postcode
  const postcode =
    careAssessmentData?.answers?.['care-recipient-postcode'] ?? '';
  if (headerDrafts.postcode !== postcode) {
    updates['care-recipient-postcode'] = headerDrafts.postcode;
  }

  return updates;
};

const InlineLink = ({
  onClick,
  children,
}: {
  onClick?: () => void;
  children: React.ReactNode;
}) => {
  if (!onClick) return <>{children}</>;
  return (
    <Box
      component='button'
      type='button'
      onClick={onClick}
      sx={{
        color: 'inherit',
        all: 'unset',
        cursor: 'pointer',
        textDecoration: 'underline',
        font: 'inherit',
      }}
    >
      {children}
    </Box>
  );
};

const KeyValueRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Stack
    direction='row'
    spacing={2}
    sx={{
      alignItems: 'baseline',
    }}
  >
    <Typography
      variant='subtitle1'
      sx={{
        color: 'text.primary',
        width: 120,
        flexShrink: 0,
        lineHeight: 1.5,
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{
        color: 'text.secondary',
        flex: 1,
        lineHeight: 1.5,
      }}
    >
      {children}
    </Box>
  </Stack>
);

const LinkedCircle = ({ c, isLast }: any) => {
  return (
    <Box component='span'>
      <InlineLink onClick={c.onClick}>{c.name}</InlineLink>
      {isLast ? '' : ', '}
    </Box>
  );
};

const LinkedCircles = ({ circles }: any) => {
  return (
    <>
      {circles.length > 0
        ? circles.map((c: any, i: number) => (
            <LinkedCircle key={i} c={c} isLast={i === circles.length - 1} />
          ))
        : '—'}
    </>
  );
};

const OtherCircleMember = ({ m, isLast }: any) => {
  return (
    <Box component='span'>
      {m.account?.firstName} {m.account?.lastName}
      {m.careRecipientAccountRelationship?.name
        ? ` (${m.careRecipientAccountRelationship.name})`
        : ''}
      {isLast ? '' : ', '}
    </Box>
  );
};

const OtherCircleMembers = ({ members }: any) => {
  return members.length > 0
    ? members.map((m: any, i: number) => (
        <OtherCircleMember
          key={m.id ?? i}
          m={m}
          isLast={i === members.length - 1}
        />
      ))
    : '—';
};

const PrimaryCaregiverDetails = ({
  careCircle,
}: {
  careCircle: CareCircle;
}) => {
  const account = careCircle.primaryCaregiverAccount;
  const member = careCircle.careCircleMembers?.find(
    (m) => m.account?.id === account?.id,
  );

  if (!account) return null;

  return (
    <Typography
      variant='body1'
      component='span'
      sx={{
        color: 'text.secondary',
      }}
    >
      {account.firstName} {account.lastName}
      {member?.careRecipientAccountRelationship?.name
        ? ` (${member.careRecipientAccountRelationship.name})`
        : ''}
      {account.email ? ` · ${account.email}` : ''}
    </Typography>
  );
};

const CareRecipientAvatar = ({ careRecipient }: any) => {
  return (
    <Avatar
      sx={{
        bgcolor: '#D2E0A3',
        color: 'text.title',
        width: 64,
        height: 64,
        fontSize: 24,
        fontWeight: 500,
      }}
    >
      {careRecipient.initials}
    </Avatar>
  );
};

const KeyValueRowTextField = ({
  label,
  isEditing,
  onChange,
  value,
  placeholder,
  fallback,
  type,
}: any) => {
  return (
    <KeyValueRow label={label}>
      {isEditing ? (
        <TextField
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          variant='standard'
          size='small'
          sx={{ width: 240, maxWidth: '100%' }}
          slotProps={{
            htmlInput: { 'aria-label': label },
          }}
        />
      ) : (
        fallback
      )}
    </KeyValueRow>
  );
};

const EditHeaderButtons = ({
  isEditingHeader,
  handleHeaderCancel,
  handleHeaderSave,
  handleHeaderEditStart,
}: any) => {
  return isEditingHeader ? (
    <Stack direction='row' spacing={1}>
      <Button variant='text' onClick={handleHeaderCancel}>
        Cancel
      </Button>
      <Button variant='text' onClick={handleHeaderSave}>
        Save
      </Button>
    </Stack>
  ) : (
    <Button variant='text' onClick={handleHeaderEditStart}>
      Edit
    </Button>
  );
};

export const EditableCareCircleSummary = ({
  careCircle,
  linkedCircles,
  careAssessmentData,
}: any) => {
  const [isEditingHeader, setIsEditingHeader] = useState(false);

  const [headerDrafts, setHeaderDrafts] = useState<any>({});

  const conditions = parseConditions(
    careAssessmentData?.answers?.[
      'care-recipient-any-illnesses-or-disabilities'
    ],
  );
  const dob =
    careAssessmentData?.answers?.['care-recipient-date-of-birth'] ?? '';
  const postcode =
    careAssessmentData?.answers?.['care-recipient-postcode'] ?? '';

  const careRecipient = useMemo(
    () => careRecipientFromCareCircle(careCircle),
    [careCircle],
  );

  const onChangeHeader = useCallback(
    (field: string) => (e: any) =>
      setHeaderDrafts((d: any) => ({ ...d, [field]: e.target.value })),
    [setHeaderDrafts],
  );

  const handleHeaderEditStart = () => {
    setHeaderDrafts({});
    setIsEditingHeader(true);
  };

  const handleHeaderSave = () => {
    const updates = calcUpdates(headerDrafts, careAssessmentData);
    if (Object.keys(updates).length > 0) {
      careAssessmentData.createManyAnswer?.(updates);
      careAssessmentData.setAnswers?.(updates);
    }
    setIsEditingHeader(false);
  };

  const handleHeaderCancel = () => setIsEditingHeader(false);

  const otherCarerMembers = useMemo(
    () => otherCarerMembersFromCareCircle(careCircle),
    [careCircle],
  );

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={3}
      sx={{
        alignItems: 'flex-start',
      }}
    >
      <Stack
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        <CareRecipientAvatar careRecipient={careRecipient} />
        <EditHeaderButtons
          isEditingHeader={isEditingHeader}
          handleHeaderCancel={handleHeaderCancel}
          handleHeaderSave={handleHeaderSave}
          handleHeaderEditStart={handleHeaderEditStart}
        />
      </Stack>
      <Stack
        spacing={2}
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Stack
          direction='row'
          spacing={2}
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant='h4'
            sx={{
              color: 'text.title',
            }}
          >
            Caring for {careRecipient.firstName} {careRecipient.lastName}
          </Typography>
        </Stack>
        <Stack spacing={1}>
          <KeyValueRow label='Primary carer'>
            {careCircle ? (
              <PrimaryCaregiverDetails careCircle={careCircle} />
            ) : (
              '—'
            )}
          </KeyValueRow>
          {(otherCarerMembers.length > 0 || isEditingHeader) && (
            <KeyValueRow label='Other carers'>
              <OtherCircleMembers members={otherCarerMembers} />
            </KeyValueRow>
          )}
          <KeyValueRowTextField
            label='Conditions'
            isEditing={isEditingHeader}
            onChange={onChangeHeader('conditions')}
            value={headerDrafts.conditions || conditions.join(', ')}
            placeholder='e.g. Dementia, Mobility'
            fallback={conditions.join(', ') || '—'}
          />
          <KeyValueRowTextField
            label='Date of birth'
            type='date'
            isEditing={isEditingHeader}
            onChange={onChangeHeader('dob')}
            value={headerDrafts.dob || dob}
            fallback={dobDisplay(dob, computeAge(dob)) || '—'}
          />
          <KeyValueRowTextField
            label='Postcode'
            isEditing={isEditingHeader}
            onChange={onChangeHeader('postcode')}
            value={headerDrafts.postcode || postcode}
            placeholder='e.g. N19 7AF'
            fallback={postcode || '—'}
          />
          {(linkedCircles.length > 0 || isEditingHeader) && (
            <KeyValueRow label='Linked circle'>
              <LinkedCircles circles={linkedCircles} />
            </KeyValueRow>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

EditableCareCircleSummary.displayName = 'EditableCareCircleSummary';
export default EditableCareCircleSummary;
